import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "./api";

/* ============================
   🔹 1) Fetch Daily Stats
=============================== */
export const fetchDailyStats = createAsyncThunk(
  "stats/fetchDailyStats",
  async ({ date, specialty }, { rejectWithValue }) => {
    try {
      const params = {};
      if (date) params.date = date;
      if (specialty) params.specialty = specialty;

      const response = await api.get(`/api/orders/stats/daily/`, { params });
      const data = response.data ?? {};

      if (Array.isArray(data)) {
        return { stats: data, snapshot: null };
      }

      return {
        stats: data?.results ?? [],
        snapshot: data?.group_snapshot ?? null,
      };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch daily stats."
      );
    }
  }
);

/* ============================
   🔹 2) Fetch Monthly Stats
=============================== */
export const fetchMonthlyStats = createAsyncThunk(
  "stats/fetchMonthlyStats",
  async ({ month, year, specialty }, { rejectWithValue }) => {
    try {
      const params = {};
      if (month) params.month = month;
      if (year) params.year = year;
      if (specialty) params.specialty = specialty;

      const response = await api.get(`/api/orders/stats/monthly/`, { params });

      return Array.isArray(response.data) ? response.data : response.data?.results ?? [];
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch monthly stats."
      );
    }
  }
);

/* ============================
   🔹 3) Export CSV
=============================== */
export const exportStatsCSV = createAsyncThunk(
  "stats/exportCSV",
  async ({ type = "daily", ...params }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/orders/stats/export/`, {
        params: { type, ...params },
        responseType: "blob",
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to export CSV."
      );
    }
  }
);

/* ============================
   🔹 Slice
=============================== */
const initialState = {
  dailyStats: [],
  dailySnapshot: null,
  monthlyStats: [],
  csvFile: null,

  statusDaily: "idle",
  statusMonthly: "idle",
  statusCSV: "idle",

  errorDaily: null,
  errorMonthly: null,
  errorCSV: null,
};

const statsSlice = createSlice({
  name: "stats",
  initialState,
  reducers: {
    clearStats: (state) => {
      state.dailyStats = [];
       state.dailySnapshot = null;
      state.monthlyStats = [];
    },
  },
  extraReducers: (builder) => {
    builder
      /* ---- DAILY ---- */
      .addCase(fetchDailyStats.pending, (state) => {
        state.statusDaily = "loading";
        state.errorDaily = null;
        state.dailySnapshot = null;
      })
      .addCase(fetchDailyStats.fulfilled, (state, action) => {
        state.statusDaily = "succeeded";
        state.dailyStats = action.payload.stats;
        state.dailySnapshot = action.payload.snapshot;
      })
      .addCase(fetchDailyStats.rejected, (state, action) => {
        state.statusDaily = "failed";
        state.errorDaily = action.payload;
        state.dailyStats = [];
        state.dailySnapshot = null;
      })

      /* ---- MONTHLY ---- */
      .addCase(fetchMonthlyStats.pending, (state) => {
        state.statusMonthly = "loading";
        state.errorMonthly = null;
      })
      .addCase(fetchMonthlyStats.fulfilled, (state, action) => {
        state.statusMonthly = "succeeded";
        state.monthlyStats = action.payload;
      })
      .addCase(fetchMonthlyStats.rejected, (state, action) => {
        state.statusMonthly = "failed";
        state.errorMonthly = action.payload;
        state.monthlyStats = [];
      })

      /* ---- EXPORT CSV ---- */
      .addCase(exportStatsCSV.pending, (state) => {
        state.statusCSV = "loading";
        state.errorCSV = null;
      })
      .addCase(exportStatsCSV.fulfilled, (state, action) => {
        state.statusCSV = "succeeded";
        state.csvFile = action.payload;
      })
      .addCase(exportStatsCSV.rejected, (state, action) => {
        state.statusCSV = "failed";
        state.errorCSV = action.payload;
      });
  },
});

export const { clearStats } = statsSlice.actions;
export default statsSlice.reducer;
